import { createApi } from "@reduxjs/toolkit/query/react";
import { apiFunction } from "../utils";
import { baseQueryWithReauth } from "../baseQueryWithReauth";
import { serializeArgsWithoutToken } from "../serializeQueryArgs";

const dealerFirmApi = createApi({
  reducerPath: "dealerFirm",
  tagTypes: ["Firm", "RoleTemplate", "FirmBillingSummary", "FirmConsumptionSummary"],
  baseQuery: baseQueryWithReauth,
  serializeQueryArgs: serializeArgsWithoutToken, // #142: token önbellek anahtarına girmez
  keepUnusedDataFor: 120,
  endpoints(builder) {
    return {
      getDealerRolesFirms: builder.query({
        query: (data) => apiFunction(data, "/api/firms/dealerRoles/getFirms", "POST"),
        providesTags: ["Firm"],
      }),
      getDealerRolesFirmByID: builder.query({
        query: (data) =>
          apiFunction(data, "/api/firms/dealerRoles/getFirmByID", "POST"),
        providesTags: ["Firm"],
      }),
      getDealerRolesBelowFirms: builder.query({
        query: (data) =>
          apiFunction(data, "/api/firms/dealerRoles/getBelowFirms", "POST"),
        providesTags: ["Firm"],
      }),
      getDealerRolesCustomersByFirmId: builder.query({
        query: (data) =>
          apiFunction(data, "/api/firms/dealerRoles/getCustomersByFirmId", "POST"),
        providesTags: ["Firm"],
      }),
      getDealerRolesUsersByFirmID: builder.query({
        query: (data) =>
          apiFunction(data, "/api/firms/dealerRoles/getUsersByFirmID", "POST"),
        providesTags: ["Firm"],
      }),
      getDealerRolesDeviceByFirmID: builder.query({
        query: (data) =>
          apiFunction(data, "/api/firms/dealerRoles/getDevicesOfFirm", "POST"),
      }),
      getDealerRolesFirmSync: builder.query({
        query: (data) =>
          apiFunction(data, "/api/firms/dealerRoles/getFirmSync", "POST"),
      }),
      updateDealerRolesFirmSync: builder.mutation({
        query: (data) =>
          apiFunction(data, "/api/firms/dealerRoles/updateSync", "PATCH"),
      }),
      getDealerRolesFirmProducts: builder.query({
        query: (data) =>
          apiFunction(data, "/api/firms/dealerRoles/getFirmProducts", "POST"),
        providesTags: ["Firm"],
      }),
      getDealerRolesFirmBillingSummary: builder.query({
        query: (data) =>
          apiFunction(data, "/api/firms/dealerRoles/getFirmBillingSummary", "POST"),
        providesTags: (result, error, arg) => [
          { type: "FirmBillingSummary", id: arg?.firmId },
        ],
      }),
      getDealerRolesFirmConsumptionSummary: builder.query({
        query: (data) =>
          apiFunction(data, "/api/firms/dealerRoles/getFirmConsumptionSummary", "POST"),
        providesTags: (result, error, arg) => [
          { type: "FirmConsumptionSummary", id: arg?.firmId },
        ],
      }),
      // Bayi dashboard üst durum kutuları — bayi/müşteri/cihaz sayıları.
      // Kapsam (scope) seçicisinden bağımsız: her zaman tüm bayi ağacını özetler.
      // data.quota = bayinin kendi kotası; alt firmaya kota gönderimi (Firm
      // invalidate) sonrası tazelensin.
      getDealerRolesOverview: builder.query({
        query: (data) =>
          apiFunction(data, "/api/firms/dealerRoles/getDealerOverview", "POST"),
        providesTags: ["Firm"],
      }),
      // Raporlar sekmesi → Tüketim (#343)
      getDealerRolesFirmDetailPerformanceReport: builder.mutation({
        query: (data) =>
          apiFunction(data, `/api/firmLog/dealerRoles/performance-report`, "POST"),
      }),
      getDealerRolesFirmLogReport: builder.mutation({
        query: (data) => apiFunction(data, `/api/firmLog/dealerRoles/report`, "POST"),
      }),
      getDealerRolesFirmConsumentLogReport: builder.query({
        query: (data) =>
          apiFunction(data, `/api/firmLog/dealerRoles/report/consument`, "POST"),
      }),
      // Role Template endpoints
      getDealerRolesRoleTemplates: builder.query({
        query: (data) =>
          apiFunction(data, "/api/firms/dealerRoles/roleTemplates/getRoleTemplates", "POST"),
        providesTags: ["RoleTemplate"],
        keepUnusedDataFor: 0,
      }),
      createDealerRolesRoleTemplate: builder.mutation({
        query: (data) =>
          apiFunction(data, "/api/firms/dealerRoles/roleTemplates/createRoleTemplate", "POST"),
        invalidatesTags: ["RoleTemplate"],
      }),
      updateDealerRolesRoleTemplate: builder.mutation({
        query: (data) =>
          apiFunction(data, "/api/firms/dealerRoles/roleTemplates/updateRoleTemplate", "PATCH"),
        invalidatesTags: ["RoleTemplate"],
      }),
      deleteDealerRolesRoleTemplate: builder.mutation({
        query: (data) =>
          apiFunction(data, "/api/firms/dealerRoles/roleTemplates/deleteRoleTemplate", "DELETE"),
        invalidatesTags: ["RoleTemplate"],
      }),
      // SubFirm endpoints
      searchDealerUnlinkedFirms: builder.mutation({
        query: (data) =>
          apiFunction(data, "/api/firms/dealerRoles/subfirms/searchUnlinkedFirms", "POST"),
      }),
      linkDealerSubFirm: builder.mutation({
        query: (data) =>
          apiFunction(data, "/api/firms/dealerRoles/subfirms/linkSubFirm", "POST"),
        invalidatesTags: ["Firm"],
      }),
      unlinkDealerSubFirm: builder.mutation({
        query: (data) =>
          apiFunction(data, "/api/firms/dealerRoles/subfirms/unlinkSubFirm", "POST"),
        invalidatesTags: ["Firm"],
      }),
      // ALT BAYIYE KOTA GONDER — "Kota Paylasimi" (storage muadili:
      // storageRoles/firmApi.js sendSubFirmQuota)
      sendDealerSubFirmQuota: builder.mutation({
        query: (data) =>
          apiFunction(data, "/api/firms/dealerRoles/sendSubFirmQuota", "PATCH"),
        // "Bills" bu slice'in tagTypes'inda YOK (bayi faturalari
        // dealerRoles/billsApi'de) — tanimsiz tag invalidate edilemez.
        invalidatesTags: (result, error, arg) => [
          "Firm",
          { type: "FirmBillingSummary", id: arg?.id },
          { type: "FirmBillingSummary", id: arg?.subFirmId },
          { type: "FirmConsumptionSummary", id: arg?.id },
          { type: "FirmConsumptionSummary", id: arg?.subFirmId },
        ],
      }),
      getDealerSendableSubFirmQuota: builder.query({
        query: (data) =>
          apiFunction(data, "/api/firms/dealerRoles/sendableSubFirmQuota", "POST"),
        providesTags: ["Firm"],
      }),
      // Firma meşguliyeti (#303) — kilidi tutulan firmalar. Polling ile okunur;
      // cache etiketi yok (anlık durum).
      getDealerFirmLockStatus: builder.query({
        query: (data) =>
          apiFunction(data, "/api/firms/dealerRoles/firmLockStatus", "POST"),
        keepUnusedDataFor: 0,
      }),
    };
  },
});

export const {
  useGetDealerRolesFirmsQuery,
  useLazyGetDealerRolesFirmsQuery,
  useGetDealerRolesFirmByIDQuery,
  useLazyGetDealerRolesFirmByIDQuery,
  useGetDealerRolesBelowFirmsQuery,
  useGetDealerRolesCustomersByFirmIdQuery,
  useGetDealerRolesUsersByFirmIDQuery,
  useGetDealerRolesDeviceByFirmIDQuery,
  useLazyGetDealerRolesDeviceByFirmIDQuery,
  useGetDealerRolesFirmSyncQuery,
  useLazyGetDealerRolesFirmSyncQuery,
  useUpdateDealerRolesFirmSyncMutation,
  useGetDealerRolesFirmProductsQuery,
  useLazyGetDealerRolesFirmProductsQuery,
  useGetDealerRolesFirmBillingSummaryQuery,
  useGetDealerRolesFirmConsumptionSummaryQuery,
  useGetDealerRolesOverviewQuery,
  useGetDealerRolesFirmLogReportMutation,
  useGetDealerRolesFirmDetailPerformanceReportMutation,
  useGetDealerRolesFirmConsumentLogReportQuery,
  useGetDealerRolesRoleTemplatesQuery,
  useLazyGetDealerRolesRoleTemplatesQuery,
  useCreateDealerRolesRoleTemplateMutation,
  useUpdateDealerRolesRoleTemplateMutation,
  useDeleteDealerRolesRoleTemplateMutation,
  useSearchDealerUnlinkedFirmsMutation,
  useLinkDealerSubFirmMutation,
  useUnlinkDealerSubFirmMutation,
  useSendDealerSubFirmQuotaMutation,
  useGetDealerSendableSubFirmQuotaQuery,
  useLazyGetDealerSendableSubFirmQuotaQuery,
  useGetDealerFirmLockStatusQuery,
} = dealerFirmApi;
export { dealerFirmApi };
